import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  AudienceType,
  ContestUpgrade,
  Currency,
  Equipment,
  Skill,
} from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import { AmountRange } from '@freelancer/pipes';
import { CardLevel } from '@freelancer/ui/card';
import { HorizontalAlignment } from '@freelancer/ui/grid';
import { HeadingType } from '@freelancer/ui/heading';
import { IconSize } from '@freelancer/ui/icon';
import { ListItemPadding } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import { TagType } from '@freelancer/ui/tag';
import {
  FontType,
  FontWeight,
  TextAlign,
  TextSize,
  TextTransform,
} from '@freelancer/ui/text';
import { UpgradeType } from '@freelancer/ui/upgrade-tag';
import { toNumber } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { BudgetType } from '../../project-budget/budget-type.component';
import { CUSTOM_BUDGET_ID } from '../../project-budget/budget.component';
import {
  DurationType,
  URGENT_CONTEST_DURATION,
} from '../contest/duration-type/duration-type.component';
import { StepName } from '../form.component';
import { JobType } from '../job-type/job-type.component';
import { ProjectAdvancedUpgrades } from '../project/upgrades/upgrades.component';

export interface SummaryDetails {
  budget: number | AmountRange;
  description: string;
  duration: number;
  jobType: JobType;
  projectBudgetType: BudgetType;
  projectEquipment: ReadonlyArray<Equipment>;
  skills: ReadonlyArray<Skill>;
  title: string;
  upgrades: ReadonlyArray<string>;
  audienceSelect?: AudienceType;
  billingCode?: string;
  projectDate?: string;
  projectLocation?: string;
  projectEndLocation?: string;
}

@Component({
  selector: 'app-summary',
  template: `
    <fl-heading
      i18n="Post Job Page form summary"
      [headingType]="HeadingType.H3"
      [flMarginBottom]="Margin.XXXSMALL"
      [size]="TextSize.MID"
    >
      Are these details correct?
    </fl-heading>
    <fl-card
      *ngIf="summaryDetails$ | async as summaryDetails"
      [edgeToEdge]="true"
      [flMarginBottom]="Margin.LARGE"
      [level]="CardLevel.SECONDARY"
    >
      <fl-list [padding]="ListItemPadding.MID">
        <fl-list-item>
          <fl-grid>
            <fl-col
              class="Header"
              [col]="12"
              [colTablet]="3"
              [flMarginBottom]="Margin.XSMALL"
              [flMarginBottomTablet]="Margin.NONE"
            >
              <fl-grid>
                <fl-col
                  class="Header-pic"
                  [col]="3"
                  [colTablet]="12"
                  [flMarginBottom]="Margin.XSMALL"
                >
                  <fl-bit [ngSwitch]="summaryDetails.jobType">
                    <fl-picture
                      [boundedWidth]="true"
                      [src]="'job-post/project-icon.svg'"
                      alt="Online project icon"
                      i18n-alt="Project type icon alt text"
                      *ngSwitchCase="JobType.PROJECT"
                    ></fl-picture>
                    <fl-picture
                      [boundedWidth]="true"
                      [src]="'job-post/post-project-local.svg'"
                      alt="Local project icon"
                      i18n-alt="Local type icon alt text"
                      *ngSwitchCase="JobType.LOCAL"
                    ></fl-picture>
                    <fl-picture
                      [boundedWidth]="true"
                      [src]="'job-post/contest-icon.svg'"
                      alt="Contest icon"
                      i18n-alt="Contest icon alt text"
                      *ngSwitchCase="JobType.CONTEST"
                    ></fl-picture>
                  </fl-bit>
                </fl-col>
                <fl-col class="Header-text" [col]="9" [colTablet]="12">
                  <fl-bit [ngSwitch]="summaryDetails.jobType">
                    <fl-text
                      *ngSwitchCase="JobType.PROJECT"
                      i18n="Post job page project type"
                      [textTransform]="TextTransform.UPPERCASE"
                      [flMarginBottom]="Margin.XXXSMALL"
                    >
                      Project
                    </fl-text>
                    <fl-text
                      *ngSwitchCase="JobType.LOCAL"
                      i18n="Post job page project type"
                      [textTransform]="TextTransform.UPPERCASE"
                      [flMarginBottom]="Margin.XXXSMALL"
                    >
                      Local
                    </fl-text>
                    <fl-text
                      *ngSwitchCase="JobType.CONTEST"
                      i18n="Post job page project type"
                      [textTransform]="TextTransform.UPPERCASE"
                      [flMarginBottom]="Margin.XXXSMALL"
                    >
                      {{ summaryDetails.duration }} day contest
                    </fl-text>
                  </fl-bit>
                  <ng-container
                    *ngIf="
                      (summaryDetails.jobType === JobType.PROJECT ||
                        summaryDetails.jobType === JobType.LOCAL) &&
                      (showBudget$ | async)
                    "
                    [ngSwitch]="summaryDetails.projectBudgetType"
                  >
                    <fl-text
                      i18n="
                         Post job page fixed project budget in summary section
                      "
                      *ngSwitchCase="BudgetType.FIXED"
                      [weight]="FontWeight.BOLD"
                    >
                      {{ summaryDetails.budget | flCurrency: currency.code }}
                    </fl-text>
                    <fl-text
                      i18n="
                         Post job page hourly project budget in summary section
                      "
                      *ngSwitchCase="BudgetType.HOURLY"
                      [weight]="FontWeight.BOLD"
                    >
                      {{ summaryDetails.budget | flCurrency: currency.code }}
                      per hour
                    </fl-text>
                  </ng-container>
                  <fl-text
                    i18n="Post job page contest budget in summary section"
                    *ngIf="summaryDetails.jobType === JobType.CONTEST"
                    [weight]="FontWeight.BOLD"
                  >
                    {{ summaryDetails.budget | flCurrency: currency.code }}
                  </fl-text>
                </fl-col>
              </fl-grid>
            </fl-col>
            <fl-col class="Body" [col]="12" [colTablet]="9">
              <fl-text
                class="Body-title"
                [flMarginBottom]="Margin.XXSMALL"
                [size]="TextSize.SMALL"
                [weight]="FontWeight.BOLD"
              >
                {{ summaryDetails.title }}
              </fl-text>
              <fl-bit [flMarginBottom]="Margin.SMALL">
                <fl-interactive-text
                  class="Body-description"
                  [content]="summaryDetails.description"
                  [fontSize]="TextSize.XSMALL"
                ></fl-interactive-text>
              </fl-bit>
              <fl-bit
                *ngIf="summaryDetails.projectDate"
                class="Body-projectDate"
                [flMarginBottom]="Margin.SMALL"
              >
                <fl-bit
                  class="Body-projectDate-icon"
                  [flMarginRight]="Margin.XXSMALL"
                >
                  <fl-icon
                    [name]="'ui-calendar-v2'"
                    [size]="IconSize.SMALL"
                  ></fl-icon>
                </fl-bit>
                <fl-text>
                  {{ summaryDetails.projectDate | date: 'MMM d, y' }}
                </fl-text>
              </fl-bit>
              <fl-bit
                *ngIf="summaryDetails.projectLocation"
                [flMarginBottom]="Margin.SMALL"
              >
                <app-project-location-details
                  [displayLocation]="summaryDetails.projectLocation"
                  [displayEndLocation]="summaryDetails.projectEndLocation"
                >
                </app-project-location-details>
              </fl-bit>
              <fl-bit
                *ngIf="
                  summaryDetails.projectEquipment &&
                  summaryDetails.projectEquipment.length > 0 &&
                  summaryDetails.jobType === JobType.LOCAL
                "
              >
                <fl-text
                  i18n="Post Job Page summary - equipment field header"
                  [flMarginBottom]="Margin.XXSMALL"
                  [size]="TextSize.XXSMALL"
                  [weight]="FontWeight.BOLD"
                >
                  Equipment
                </fl-text>
                <fl-bit class="Body-tag" [flMarginBottom]="Margin.SMALL">
                  <fl-tag
                    *ngFor="let equipment of summaryDetails.projectEquipment"
                    [flMarginRight]="Margin.XXXSMALL"
                    [flMarginBottom]="Margin.XXXSMALL"
                    [type]="TagType.DEFAULT"
                  >
                    {{ equipment }}
                  </fl-tag>
                </fl-bit>
              </fl-bit>
              <fl-text
                *ngIf="
                  summaryDetails.jobType === JobType.LOCAL &&
                  summaryDetails.projectEquipment &&
                  summaryDetails.projectEquipment.length > 0
                "
                i18n="Post Job Page summary - skills field header"
                [flMarginBottom]="Margin.XXSMALL"
                [size]="TextSize.XXSMALL"
                [weight]="FontWeight.BOLD"
              >
                Skills
              </fl-text>
              <fl-bit class="Body-tag" [flMarginBottom]="Margin.SMALL">
                <fl-tag
                  *ngFor="let skill of summaryDetails.skills"
                  [flMarginRight]="Margin.XXXSMALL"
                  [flMarginBottom]="Margin.XXXSMALL"
                  [type]="TagType.DEFAULT"
                >
                  {{ skill.name }}
                </fl-tag>
              </fl-bit>
              <fl-upgrade-tag
                *ngFor="let upgrade of summaryDetails.upgrades"
                class="UpgradeItem-tag"
                [flMarginRight]="Margin.XXXSMALL"
                [flMarginBottom]="Margin.XXXSMALL"
                [upgradeType]="getUpgradeType(upgrade)"
              >
              </fl-upgrade-tag>
              <ng-container *flFeature="Feature.BILLING_CODE">
                <fl-bit
                  *ngIf="
                    (summaryDetails.jobType === JobType.PROJECT ||
                      summaryDetails.jobType === JobType.LOCAL) &&
                    summaryDetails.billingCode
                  "
                  class="Body-billingCode"
                >
                  <fl-text
                    i18n="
                       Post job page project billing code label in summary
                      section
                    "
                    [flMarginRight]="Margin.XXSMALL"
                  >
                    BILLING CODE:
                  </fl-text>
                  <fl-text [weight]="FontWeight.BOLD">
                    {{ summaryDetails.billingCode }}
                  </fl-text>
                </fl-bit>
              </ng-container>
              <ng-container *ngIf="summaryDetails.audienceSelect">
                <fl-bit [ngSwitch]="summaryDetails.audienceSelect">
                  <fl-grid
                    *ngSwitchCase="AudienceType.FLNLTD"
                    [hAlign]="HorizontalAlignment.HORIZONTAL_CENTER"
                  >
                    <fl-col [col]="2" [colTablet]="3">
                      <fl-picture
                        alt="Freelancer user icon"
                        i18n-alt="Freelancer icon alt text"
                        [src]="'flnltd-logo-icon.svg'"
                        [boundedHeight]="true"
                        [boundedWidth]="true"
                      ></fl-picture>
                    </fl-col>
                    <fl-col class="Body" [col]="10" [colTablet]="9">
                      <fl-text
                        i18n="Freelancer only audience description"
                        [weight]="FontWeight.BOLD"
                        [size]="TextSize.MID"
                      >
                        Receive bids from Freelancer employees only.
                      </fl-text>
                    </fl-col>
                  </fl-grid>

                  <fl-grid
                    *ngSwitchCase="AudienceType.FLNLTD_AND_MARKETPLACE"
                    [hAlign]="HorizontalAlignment.HORIZONTAL_CENTER"
                  >
                    <fl-col [col]="2" [colTablet]="3">
                      <fl-picture
                        alt="Freelancer user icon"
                        i18n-alt="FlnLtd and Freelancer icon alt text"
                        [src]="'flnltd-and-regular-freelancer-logo.svg'"
                        [boundedHeight]="true"
                        [boundedWidth]="true"
                        [alignCenter]="true"
                      ></fl-picture>
                    </fl-col>
                    <fl-col [col]="10" [colTablet]="9">
                      <fl-text
                        i18n="FlnLtd and Freelancer audience description"
                        [weight]="FontWeight.BOLD"
                        [size]="TextSize.MID"
                      >
                        Receive bids from both Freelancer employees and external
                        freelancers.
                      </fl-text>
                    </fl-col>
                  </fl-grid>
                </fl-bit>
              </ng-container>
            </fl-col>
          </fl-grid>
        </fl-list-item>
      </fl-list>
    </fl-card>
  `,
  styleUrls: ['./summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SummaryComponent implements OnInit {
  AudienceType = AudienceType;
  BudgetType = BudgetType;
  CardLevel = CardLevel;
  CUSTOM_BUDGET_ID = CUSTOM_BUDGET_ID;
  Feature = Feature;
  FontWeight = FontWeight;
  FontType = FontType;
  TextSize = TextSize;
  HeadingType = HeadingType;
  HorizontalAlignment = HorizontalAlignment;
  IconSize = IconSize;
  JobType = JobType;
  ListItemPadding = ListItemPadding;
  Margin = Margin;
  TagType = TagType;
  TextAlign = TextAlign;
  TextTransform = TextTransform;
  UpgradeType = UpgradeType;

  @Input() currency: Currency;
  @Input() formGroup: FormGroup;

  summaryDetails$: Rx.Observable<SummaryDetails>;
  showBudget$: Rx.Observable<boolean>;

  ngOnInit() {
    this.showBudget$ = this.formGroup.valueChanges.pipe(
      map(() =>
        this.formGroup.controls[StepName.PROJECT_BUDGET].value &&
        this.formGroup.controls[StepName.PROJECT_BUDGET].value.id ===
          CUSTOM_BUDGET_ID
          ? this.formGroup.controls.customBudget.valid
          : this.formGroup.controls[StepName.PROJECT_BUDGET].valid,
      ),
      startWith(true),
    );

    this.summaryDetails$ = this.formGroup.valueChanges.pipe(
      startWith(this.formGroup.value),
      map(value => ({
        audienceSelect: value.audienceSelect,
        budget:
          value[StepName.JOB_TYPE] === JobType.PROJECT
            ? value[StepName.PROJECT_BUDGET].id === CUSTOM_BUDGET_ID
              ? {
                  minimum: value.customBudget.minimum,
                  maximum: value.customBudget.maximum,
                }
              : {
                  minimum: value[StepName.PROJECT_BUDGET].minimum,
                  maximum: value[StepName.PROJECT_BUDGET].maximum,
                }
            : toNumber(value[StepName.CONTEST_BUDGET]),
        billingCode: value.hasBillingCode ? value.billingCode : null,
        description: value.description,
        duration:
          value[StepName.CONTEST_DURATION_TYPE] === DurationType.URGENT
            ? URGENT_CONTEST_DURATION
            : toNumber(value[StepName.CONTEST_DURATION]),
        jobType:
          value[StepName.JOB_TYPE] === JobType.CONTEST
            ? JobType.CONTEST
            : value[StepName.SKILLS].length > 0 &&
              this.formGroup.value[StepName.PROJECT_IS_LOCAL] !== false &&
              this.formGroup.value[StepName.PROJECT_LOCATION]
            ? JobType.LOCAL
            : JobType.PROJECT,
        projectBudgetType: value[StepName.PROJECT_BUDGET_TYPE],
        projectEquipment:
          value[StepName.PROJECT_IS_LOCAL] !== false &&
          value[StepName.PROJECT_EQUIPMENT]
            ? value[StepName.PROJECT_EQUIPMENT]
            : null,
        skills: value[StepName.SKILLS],
        title: value[StepName.TITLE_AND_DESCRIPTION],
        upgrades:
          value[StepName.JOB_TYPE] === JobType.CONTEST
            ? this.mapUpgrades(value[StepName.CONTEST_UPGRADES])
            : this.mapUpgrades(value[StepName.PROJECT_UPGRADES]),
        projectDate:
          value[StepName.PROJECT_IS_LOCAL] !== false &&
          value[StepName.PROJECT_DATE]
            ? value[StepName.PROJECT_DATE]
            : null,
        projectLocation:
          value[StepName.PROJECT_IS_LOCAL] !== false &&
          value[StepName.PROJECT_LOCATION] &&
          value[StepName.PROJECT_LOCATION].location
            ? value[StepName.PROJECT_LOCATION].location.fullAddress
            : null,
        projectEndLocation:
          value[StepName.PROJECT_IS_LOCAL] !== false && value.endLocation
            ? value.endLocation.location.fullAddress
            : null,
      })),
    );
  }

  mapUpgrades(upgrades: ContestUpgrade | ProjectAdvancedUpgrades) {
    return upgrades
      ? Object.entries(upgrades)
          .filter(([_, value]) => value)
          .map(([key, _]) => key)
      : [];
  }

  getUpgradeType(upgradeName: string): UpgradeType {
    if (upgradeName === 'recruiter') {
      return UpgradeType.ASSISTED;
    }
    if (upgradeName === 'topContest') {
      return UpgradeType.TOP_CONTEST;
    }
    if (upgradeName === 'ipContract') {
      return UpgradeType.IP_AGREEMENT;
    }
    const upgradeType: string | undefined = Object.values(
      this.UpgradeType,
    ).find(value => value === upgradeName);
    if (upgradeType) {
      return upgradeType as UpgradeType;
    }
    throw new Error('Invalid upgrade has been chosen');
  }
}
